import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Play, Calendar } from 'lucide-react'

type Fund = { id: string; name: string; ticker: string; source: string }

type ModelsResp = {
  stress: { id: string; name: string; gpu: boolean }[]
  bridge: { configured: boolean; healthy?: boolean; url?: string; gpu_device?: string; error?: string }
  bridge_models: { id: string; family: string }[]
}

type StressRow = {
  model: string; scenario: string
  mean: number | null; var_95: number | null; es_95: number | null
  method?: string; status?: string
}

type StressResp = {
  run_id: number; n_obs: number; results: StressRow[]
  via_bridge?: boolean; bridge_runtime_ms?: number
}

// Canonical scenario list (kept in sync with ft.quant.risk/src/risk/scenarios.py).
// Users can pick whichever subset they want; server returns rows only for the ones selected.
const STANDARD_SCENARIOS = [
  'MSCI World -10%', 'SPX -10%', 'IR +25bp', 'EUR -10%', 'Vol Spike', 'WTI -10%',
]
const STYLE_SCENARIOS = ['Trend -2sd', 'Carry -2sd', 'Momentum -2sd', 'Value -2sd']
const CORR_SCENARIOS = ['Mild Correlation Stress', 'Severe Correlation Stress', 'Extreme Correlation Stress']

type MarketEvent = {
  id: string; name: string; driver: string
  start: string; end: string; description: string
}

type EventFactorContrib = {
  factor: string; beta: number; event_return: number | null; contribution: number | null
}

type EventRow = {
  event: string; name: string; driver: string
  start: string; end: string
  driver_dd: number | null; driver_ret: number | null
  fund_ret: number | null; fund_dd: number | null
  predicted_ret: number | null; beta_to_driver: number | null
  residual: number | null
  method?: string
  top_factors?: EventFactorContrib[]
  n_days: number; status: string
}

type EventResp = { run_id: number; n_obs: number; results: EventRow[] }

export default function RiskStress() {
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState('')
  const [universe, setUniverse] = useState('narrow')
  const [window, setWindow] = useState('full')
  const [scenarios, setScenarios] = useState<string[]>([...STANDARD_SCENARIOS])
  const [models, setModels] = useState<string[]>(['vine_pca', 'vine_ortho', 'polymodel_narrow'])
  const [allowGpu, setAllowGpu] = useState(false)
  const [nSamples, setNSamples] = useState(5000)
  const [selectedEvents, setSelectedEvents] = useState<string[]>([])
  const [eventMethod, setEventMethod] = useState<'driver_beta' | 'multi_factor'>('driver_beta')

  const eventsQ = useQuery<MarketEvent[]>({
    queryKey: ['risk-stats-events'],
    queryFn: () => api.get('/risk-stats/events').then(r => r.data),
  })

  const runEvents = useMutation<EventResp, unknown>({
    mutationFn: () => api.post('/risk-stats/event-stress', {
      fund_id: fundId,
      event_ids: selectedEvents,
      universe,
      time_window: window,
      method: eventMethod,
      top_k: 15,
    }).then(r => r.data),
  })

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })
  const modelsQ = useQuery<ModelsResp>({
    queryKey: ['risk-stats-models'],
    queryFn: () => api.get('/risk-stats/models').then(r => r.data),
  })

  const run = useMutation<StressResp, unknown>({
    mutationFn: () => api.post('/risk-stats/stress', {
      fund_id: fundId,
      models, scenarios,
      universe, time_window: window,
      n_samples: nSamples, allow_gpu: allowGpu,
    }).then(r => r.data),
  })

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data
      .filter(f => !s || f.name?.toLowerCase().includes(s) || f.ticker?.toLowerCase().includes(s))
      .slice(0, 300)
  }, [fundsQ.data, search])

  const toggle = <T,>(arr: T[], v: T, setter: (x: T[]) => void) => {
    setter(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v])
  }

  // Pivot: rows = scenario, cols = model, cell = mean (PnL impact)
  const { pivotRows, pivotCols } = useMemo(() => {
    if (!run.data?.results) return { pivotRows: [] as string[], pivotCols: [] as string[] }
    const rows = new Set<string>(), cols = new Set<string>()
    for (const r of run.data.results) { rows.add(r.scenario); cols.add(r.model) }
    return { pivotRows: Array.from(rows), pivotCols: Array.from(cols) }
  }, [run.data])

  const lookup = useMemo(() => {
    const m = new Map<string, StressRow>()
    for (const r of run.data?.results ?? []) m.set(`${r.scenario}|${r.model}`, r)
    return m
  }, [run.data])

  const pct = (v: number | null | undefined) => v == null ? '—' : `${(v * 100).toFixed(2)}%`
  const cls = (v: number | null | undefined) => v == null ? '' : v < 0 ? 'text-red-600' : 'text-green-600'

  const modelBoxes = modelsQ.data?.stress ?? []

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Stress Testing</h1>
        <p className="text-muted-foreground">Scenario × model matrix. Models propagate shocks through the factor covariance / causal structure.</p>
      </div>

      {/* Controls */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by name or ticker..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input flex-1"
          />
          <select
            value={fundId}
            onChange={e => setFundId(e.target.value)}
            className="input w-full md:w-[28rem]"
          >
            <option value="">-- Select fund --</option>
            {filteredFunds.map(f => (
              <option key={f.id} value={f.id}>
                {f.name} ({f.ticker}) [{f.source}]
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Universe</span>
            <select value={universe} onChange={e => setUniverse(e.target.value)} className="input">
              <option value="narrow">narrow</option>
              <option value="wide">wide</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Window</span>
            <select value={window} onChange={e => setWindow(e.target.value)} className="input">
              <option value="full">full</option>
              <option value="10y">10y</option>
              <option value="5y">5y</option>
              <option value="2y">2y</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">n samples</span>
            <input
              type="number"
              value={nSamples}
              onChange={e => setNSamples(Math.max(100, Number(e.target.value) || 0))}
              className="input w-24"
            />
          </label>
          <label className="flex items-center gap-2 text-sm" title={modelsQ.data && !modelsQ.data.bridge.configured ? 'Deploy ft.risk.bridge on a GPU host to enable (see repo deploy/ folder).' : ''}>
            <input
              type="checkbox"
              checked={allowGpu && !!modelsQ.data?.bridge.configured}
              disabled={modelsQ.data && !modelsQ.data.bridge.configured}
              onChange={e => setAllowGpu(e.target.checked)}
            />
            <span className={modelsQ.data && !modelsQ.data.bridge.configured ? 'text-muted-foreground' : ''}>Allow GPU</span>
            {modelsQ.data && !modelsQ.data.bridge.configured && (
              <span className="text-xs text-muted-foreground">· bridge not configured</span>
            )}
          </label>
        </div>

        {/* Scenarios */}
        <div>
          <div className="text-sm text-muted-foreground mb-1">Scenarios</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <ScenarioGroup title="Market" items={STANDARD_SCENARIOS} selected={scenarios} onToggle={v => toggle(scenarios, v, setScenarios)} />
            <ScenarioGroup title="Style" items={STYLE_SCENARIOS} selected={scenarios} onToggle={v => toggle(scenarios, v, setScenarios)} />
            <ScenarioGroup title="Correlation" items={CORR_SCENARIOS} selected={scenarios} onToggle={v => toggle(scenarios, v, setScenarios)} />
          </div>
        </div>

        {/* Models */}
        <div>
          <div className="text-sm text-muted-foreground mb-1">Models</div>
          <div className="flex flex-wrap gap-2">
            {modelBoxes.map(m => {
              const active = models.includes(m.id)
              const disabled = m.gpu && !allowGpu
              return (
                <button
                  key={m.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggle(models, m.id, setModels)}
                  className={
                    'text-xs px-2 py-1 rounded border transition-colors ' +
                    (disabled
                      ? 'border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed'
                      : active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-muted hover:bg-accent')
                  }
                >
                  {m.id}{m.gpu && ' · GPU'}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <button
            type="button"
            disabled={!fundId || scenarios.length === 0 || run.isPending}
            onClick={() => run.mutate()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run Stress
          </button>
          {run.data && (
            <div className="text-sm text-muted-foreground">
              run_id <span className="font-mono">{run.data.run_id}</span> · n_obs {run.data.n_obs}
              {run.data.via_bridge && <> · via bridge ({run.data.bridge_runtime_ms}ms)</>}
            </div>
          )}
          {run.isError && (
            <div className="text-sm text-destructive">error — {String((run.error as any)?.response?.data?.detail ?? run.error)}</div>
          )}
        </div>
      </div>

      {/* Historical event stress — separate workflow that compares
          model-predicted vs actually-realised PnL over a named event window. */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          <h2 className="font-semibold">Historical event stress</h2>
          <span className="text-xs text-muted-foreground ml-2">
            predicted via fund-beta × driver drawdown; compared to realised PnL
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(eventsQ.data ?? []).map(ev => {
            const active = selectedEvents.includes(ev.id)
            return (
              <label
                key={ev.id}
                className={'flex items-start gap-2 text-sm border rounded p-2 cursor-pointer ' +
                  (active ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted')}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() =>
                    setSelectedEvents(active
                      ? selectedEvents.filter(x => x !== ev.id)
                      : [...selectedEvents, ev.id])
                  }
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-medium">{ev.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {ev.start} → {ev.end} · driver <span className="font-mono">{ev.driver}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{ev.description}</div>
                </div>
              </label>
            )
          })}
        </div>
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Method</span>
            <select
              value={eventMethod}
              onChange={e => setEventMethod(e.target.value as 'driver_beta' | 'multi_factor')}
              className="input"
            >
              <option value="driver_beta">driver β (single-factor)</option>
              <option value="multi_factor">multi-factor OLS</option>
            </select>
          </label>
          <button
            type="button"
            disabled={!fundId || selectedEvents.length === 0 || runEvents.isPending}
            onClick={() => runEvents.mutate()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {runEvents.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run event stress
          </button>
          {runEvents.data && (
            <div className="text-sm text-muted-foreground">
              run_id <span className="font-mono">{runEvents.data.run_id}</span>
            </div>
          )}
          {runEvents.isError && (
            <div className="text-sm text-destructive">
              {String((runEvents.error as any)?.response?.data?.detail ?? runEvents.error)}
            </div>
          )}
        </div>

        {runEvents.data && runEvents.data.results.length > 0 && (
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left  py-2 px-3">Event</th>
                  <th className="text-right py-2 px-3">Driver DD</th>
                  <th className="text-right py-2 px-3">β to driver</th>
                  <th className="text-right py-2 px-3">Predicted</th>
                  <th className="text-right py-2 px-3">Realised</th>
                  <th className="text-right py-2 px-3">Residual</th>
                </tr>
              </thead>
              <tbody>
                {runEvents.data.results.map(r => (
                  <tr key={r.event} className="border-b border-border/50">
                    <td className="py-2 px-3 text-xs">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-muted-foreground">{r.start} → {r.end}</div>
                    </td>
                    <td className={'py-2 px-3 text-right font-mono text-xs ' + cls(r.driver_dd)}>
                      {pct(r.driver_dd)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {r.beta_to_driver == null ? '—' : r.beta_to_driver.toFixed(2)}
                    </td>
                    <td className={'py-2 px-3 text-right font-mono text-xs ' + cls(r.predicted_ret)}>
                      {pct(r.predicted_ret)}
                    </td>
                    <td className={'py-2 px-3 text-right font-mono text-xs ' + cls(r.fund_ret)}>
                      {pct(r.fund_ret)}
                    </td>
                    <td className={'py-2 px-3 text-right font-mono text-xs ' + cls(r.residual)}>
                      {pct(r.residual)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-muted-foreground mt-2">
              Residual = realised − predicted. Big negative residual = model underestimated the event.
              Switch Method to <span className="font-mono">multi-factor OLS</span> to attribute the event to specific factor moves.
            </div>

            {eventMethod === 'multi_factor' && runEvents.data.results.some(r => (r.top_factors?.length ?? 0) > 0) && (
              <div className="mt-4 space-y-3">
                {runEvents.data.results.filter(r => (r.top_factors?.length ?? 0) > 0).map(r => (
                  <div key={'c_' + r.event} className="border border-border rounded p-3">
                    <div className="text-xs font-medium mb-2">{r.name} — top factor contributions</div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-muted-foreground">
                          <th className="text-left py-1 px-2">Factor</th>
                          <th className="text-right py-1 px-2">β</th>
                          <th className="text-right py-1 px-2">Event return</th>
                          <th className="text-right py-1 px-2">Contribution to pred</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.top_factors!.map(tf => (
                          <tr key={tf.factor} className="border-t border-border/40">
                            <td className="py-1 px-2 font-mono">{tf.factor}</td>
                            <td className="py-1 px-2 text-right font-mono">{tf.beta.toFixed(2)}</td>
                            <td className={'py-1 px-2 text-right font-mono ' + cls(tf.event_return)}>{pct(tf.event_return)}</td>
                            <td className={'py-1 px-2 text-right font-mono ' + cls(tf.contribution)}>{pct(tf.contribution)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Matrix */}
      {pivotRows.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h2 className="font-semibold mb-3">Scenario × Model — mean PnL impact</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Scenario</th>
                  {pivotCols.map(m => (
                    <th key={m} className="text-right py-2 px-3 font-mono text-xs whitespace-nowrap">{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pivotRows.map(s => (
                  <tr key={s} className="border-b border-border/50">
                    <td className="py-2 px-3 text-xs">{s}</td>
                    {pivotCols.map(m => {
                      const r = lookup.get(`${s}|${m}`)
                      return (
                        <td key={m} className={'py-2 px-3 text-right font-mono text-xs ' + cls(r?.mean)}>
                          {pct(r?.mean)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-muted-foreground mt-3">
            Negative = fund loses money under the scenario. VaR 95% / ES 95% are also persisted per cell (see Runs).
          </div>
        </div>
      )}
    </div>
  )
}

function ScenarioGroup({
  title, items, selected, onToggle,
}: { title: string; items: string[]; selected: string[]; onToggle: (v: string) => void }) {
  return (
    <div className="border border-border rounded p-3">
      <div className="text-xs uppercase text-muted-foreground mb-2">{title}</div>
      <div className="space-y-1">
        {items.map(s => (
          <label key={s} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={selected.includes(s)} onChange={() => onToggle(s)} />
            <span>{s}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
